import { useMemo, useRef } from 'react';
import { Commands1 } from '@frontend/api-commands';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Table, TableInstance, useAlert, useModalControl } from '@frontend/design-system';
import { useGenerateColumns } from '../../../hooks';
import { SummaryItem } from '../../SummaryItem';
import { BulkPaymentRequestModal } from '../BulkRequestModal';
import { CollectionColumn, generateColumns } from './generate-columns';
import { TableTopToolbar } from './top-toolbar';

export const CollectionList = () => {
  const columns = useGenerateColumns<CollectionColumn>(generateColumns);
  const { selectedLocationIds } = useAppScopeStore();
  const tableRef = useRef<TableInstance<CollectionColumn>>(null);
  const alert = useAlert();

  const { modalProps, triggerProps } = useModalControl();

  const { data: collections, isLoading } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      onError: () => alert.error('Error fetching collections'),
      refetchOnMount: 'always',
    },
  });

  const totalAmountToCollect = useMemo(
    () => collections?.patientBalances?.reduce((acc, collection) => acc + (collection.balance ?? 0), 0),
    [collections]
  );

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        '.table-toolbar': {
          paddingLeft: 0,
        },
      }}
    >
      <CollectionsSummary balance={totalAmountToCollect} />
      <Table
        isLoading={isLoading}
        colConfig={columns}
        data={collections?.patientBalances ?? []}
        hasResponsiveColWidths
        instanceRef={tableRef}
        isSelectable
        customToolbarRender={(tableInstance) => (
          <TableTopToolbar
            tableInstance={tableInstance}
            syncDate={collections?.requestedAt}
            triggerProps={triggerProps}
          />
        )}
        rowSelectionConfig={{
          hideBulkSelection: true,
          isRowDisabled: ({ locationIds }) => locationIds?.length > 1,
        }}
      />
      <BulkPaymentRequestModal
        modalProps={modalProps}
        selected={
          tableRef.current?.selectedFlatRows.map(({ original }: { original: CollectionColumn }) => original) || []
        }
      />
    </div>
  );
};

const CollectionsSummary = ({ balance }: { balance?: number }) => {
  const { t } = useTranslation('payments');
  return (
    <SummaryItem
      label={t('Total Ready to Collect')}
      amount={balance}
      loading={false}
      data-trackingid='pay-portal-invoices-btn-amountreadyocollect'
    />
  );
};
